import { createContext, useContext, useState, useEffect } from 'react'

const ChatContext = createContext()

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}

export const ChatProvider = ({ children }) => {
  const [chats, setChats] = useState([])
  const [activeChat, setActiveChat] = useState(null)
  const [isTyping, setIsTyping] = useState(false)
  const [chatHistory, setChatHistory] = useState([])

  useEffect(() => {
    // Load saved chats from localStorage
    const savedChats = localStorage.getItem('aiChats')
    if (savedChats) {
      try {
        setChats(JSON.parse(savedChats))
      } catch (error) {
        console.error('Error loading saved chats:', error)
      }
    }
  }, [])

  useEffect(() => {
    // Save chats to localStorage whenever chats change
    localStorage.setItem('aiChats', JSON.stringify(chats))
  }, [chats])

  const createNewChat = (title = 'New Chat') => {
    const newChat = {
      id: Date.now().toString(),
      title,
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    setChats(prev => [newChat, ...prev])
    setActiveChat(newChat.id)
    return newChat
  }

  const sendMessage = async (message, chatId = null) => {
    let currentChatId = chatId || activeChat
    
    if (!currentChatId) {
      const newChat = createNewChat()
      currentChatId = newChat.id
    }

    const userMessage = {
      id: Date.now().toString(),
      content: message,
      sender: 'user',
      timestamp: new Date().toISOString()
    }

    // Add user message to chat
    setChats(prev => prev.map(chat => 
      chat.id === currentChatId 
        ? { ...chat, messages: [...chat.messages, userMessage], updatedAt: new Date().toISOString() }
        : chat
    ))

    // Simulate AI response
    setIsTyping(true)
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      const aiResponse = await generateAIResponse(message)
      
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        sender: 'ai',
        timestamp: new Date().toISOString()
      }

      // Add AI response to chat
      setChats(prev => prev.map(chat => 
        chat.id === currentChatId 
          ? { ...chat, messages: [...chat.messages, aiMessage], updatedAt: new Date().toISOString() }
          : chat
      ))

    } catch (error) {
      console.error('Error generating AI response:', error)
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again.',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true
      }

      setChats(prev => prev.map(chat => 
        chat.id === currentChatId 
          ? { ...chat, messages: [...chat.messages, errorMessage], updatedAt: new Date().toISOString() }
          : chat
      ))
    } finally {
      setIsTyping(false)
    }
  }

  const generateAIResponse = async (message) => {
    // Mock AI responses based on keywords
    const responses = [
      "I understand you're asking about AI chatbots. How can I help you with your specific needs?",
      "That's an interesting question! Let me provide you with some insights about AI technology.",
      "I'd be happy to help you with that. Could you provide more details about what you're looking for?",
      "Based on your query, I can suggest several approaches to solve this problem.",
      "Great question! AI chatbots can be incredibly powerful tools for customer service and automation.",
      "I'm here to assist you with any questions about our AI chatbot platform. What would you like to know?",
      "That's a common use case for AI chatbots. Let me explain how we can help you implement this.",
      "I can help you understand the benefits and capabilities of our AI chatbot solution."
    ]
    
    // Simple keyword matching for more relevant responses
    const lowerMessage = message.toLowerCase()
    
    if (lowerMessage.includes('pricing') || lowerMessage.includes('cost')) {
      return "Our AI chatbot platform offers flexible pricing plans starting from $29/month. Would you like me to send you detailed pricing information?"
    } else if (lowerMessage.includes('features') || lowerMessage.includes('capabilities')) {
      return "Our AI chatbot platform includes features like natural language processing, multi-language support, analytics dashboard, and custom integrations. What specific features are you most interested in?"
    } else if (lowerMessage.includes('integration') || lowerMessage.includes('api')) {
      return "We provide comprehensive APIs and integration options for popular platforms like Slack, Discord, WhatsApp, and custom websites. I can help you with the integration process."
    } else if (lowerMessage.includes('demo') || lowerMessage.includes('trial')) {
      return "I'd be happy to set up a demo for you! We offer a 14-day free trial with full access to all features. Would you like me to schedule a demo call?"
    } else {
      return responses[Math.floor(Math.random() * responses.length)]
    }
  }

  const deleteChat = (chatId) => {
    setChats(prev => prev.filter(chat => chat.id !== chatId))
    if (activeChat === chatId) {
      setActiveChat(null)
    }
  }

  const updateChatTitle = (chatId, newTitle) => {
    setChats(prev => prev.map(chat => 
      chat.id === chatId 
        ? { ...chat, title: newTitle, updatedAt: new Date().toISOString() }
        : chat
    ))
  }

  const getActiveChat = () => {
    return chats.find(chat => chat.id === activeChat)
  }

  const value = {
    chats,
    activeChat,
    isTyping,
    chatHistory,
    createNewChat,
    sendMessage,
    deleteChat,
    updateChatTitle,
    setActiveChat,
    getActiveChat
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}
